export const supportsWebPush = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

export const isStandaloneDisplayMode = (): boolean => {
  if (typeof window === 'undefined') return false;

  const navigatorStandalone =
    'standalone' in window.navigator &&
    Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;

  return navigatorStandalone || displayModeStandalone;
};

export const isIosPushInstallRequired = (): boolean => {
  if (typeof window === 'undefined') return false;

  const userAgent = window.navigator.userAgent || '';
  const isAppleWebKit = /AppleWebKit/i.test(userAgent);
  const isiOSDevice = /iPhone|iPad|iPod/i.test(userAgent);
  const isiPadDesktopClass =
    /Macintosh/i.test(userAgent) &&
    typeof window.navigator.maxTouchPoints === 'number' &&
    window.navigator.maxTouchPoints > 1;

  if (!(isAppleWebKit && (isiOSDevice || isiPadDesktopClass))) {
    return false;
  }

  return !isStandaloneDisplayMode();
};

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

export const registerPushServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!supportsWebPush()) return null;

  const registration = await navigator.serviceWorker.register('/sw.js', {
    scope: '/',
    updateViaCache: 'none',
  });

  return registration;
};

export const getCurrentPushSubscription = async (): Promise<PushSubscription | null> => {
  if (!supportsWebPush()) return null;

  const registration = await registerPushServiceWorker();
  if (!registration) return null;

  return registration.pushManager.getSubscription();
};

export const subscribeCurrentDeviceToPush = async (vapidPublicKey: string): Promise<PushSubscription> => {
  const registration = await registerPushServiceWorker();
  if (!registration) {
    throw new Error('This browser does not support Web Push.');
  }

  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    return existingSubscription;
  }

  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
};

export const unsubscribeCurrentDeviceFromPush = async (): Promise<{ endpoint: string | null }> => {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return { endpoint: null };
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe().catch(() => undefined);
  return { endpoint };
};

export const getPushPermissionState = (): NotificationPermission | null => {
  if (typeof window === 'undefined' || !('Notification' in window)) return null;
  return Notification.permission;
};

export const requestPushPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    throw new Error('Notifications are not supported in this browser.');
  }

  return Notification.requestPermission();
};

export const detectPushClientPlatform = (): string => {
  if (typeof window === 'undefined') return 'unknown';

  if (isIosPushInstallRequired()) return 'ios_safari';
  if (isStandaloneDisplayMode()) return 'standalone_pwa';

  const userAgent = window.navigator.userAgent || '';
  if (/Android/i.test(userAgent)) return 'android_browser';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios_browser';
  if (/Macintosh/i.test(userAgent)) return 'mac_browser';
  if (/Windows/i.test(userAgent)) return 'windows_browser';
  return 'browser';
};
